// src/components/shell/AccountSheet.js
// Feuille du bas (bottom sheet) ouverte depuis la carte compte du drawer mobile :
// compte, paramètres, aide, thème, déconnexion. Équivalent mobile du UserMenu desktop.
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, HelpCircle, LogOut, Moon, Sun, ExternalLink } from "lucide-react";
import { SHELL_LABELS } from "./navConfig";
import { useTheme } from "../../contexts/ThemeContext";
import { toggleThemeWave } from "../../theme";
import useMe, { logout } from "../../hooks/useMe";
import LanguageSwitcher from "./LanguageSwitcher";
import UserAvatar from "./UserAvatar";
import { openFullMarginProfile } from "../../utils/fullmargin";

export default function AccountSheet({ open, onClose }) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user, initials } = useMe();
  // Abonné à i18next → le sheet se retraduit en direct au changement de langue
  // (avant : SHELL_LABELS[pickLang()] figé au montage → il fallait rouvrir).
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
  const t = SHELL_LABELS[lang];

  // Échap pour fermer (et clavier physique sur tablette).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (to) => {
    navigate(to);
    onClose?.();
  };

  // Le profil vit sur FullMargin (compte unique) : plus de page compte ici.
  const goFullMarginProfile = () => {
    openFullMarginProfile();
    onClose?.();
  };

  const Row = ({ icon: Icon, label, onClick, danger, trailing: Trailing }) => (
    <button
      onClick={onClick}
      className={[
        "fm-tap flex w-full items-center gap-3.5 rounded-2xl px-4 py-3.5 text-left text-[15px] font-medium",
        danger
          ? "text-rose-600 dark:text-rose-300"
          : "text-slate-700 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-white/5",
      ].join(" ")}
    >
      <Icon size={19} className={danger ? "" : "text-slate-400 dark:text-slate-400"} />
      <span className="flex-1">{label}</span>
      {Trailing && <Trailing size={15} className="shrink-0 text-slate-400 dark:text-slate-500" />}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[70] lg:hidden" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute inset-x-0 bottom-0 animate-fadeIn rounded-t-3xl bg-white pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl dark:bg-[#181a2e]">
        {/* Poignée : repère natif indiquant qu'on peut refermer. */}
        <div className="flex justify-center pt-2.5 pb-1">
          <span className="h-1.5 w-10 rounded-full bg-slate-300 dark:bg-white/20" />
        </div>

        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4 dark:border-white/5">
          <UserAvatar photo={user?.photo} initials={initials} size={48} className="text-base" />
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold text-slate-900 dark:text-white">
              {user?.username || t.account}
            </div>
            <div className="truncate text-xs text-slate-400 dark:text-slate-400">
              {user?.email || <span className="capitalize">{user?.plan || "free"}</span>}
            </div>
          </div>
        </div>

        <div className="p-2">
          <Row
            icon={User}
            label={t.account}
            trailing={ExternalLink}
            onClick={goFullMarginProfile}
          />
          <Row icon={HelpCircle} label={t.help} onClick={() => go("/help")} />
          <Row
            icon={isDarkMode ? Sun : Moon}
            label={isDarkMode ? t.themeLight : t.themeDark}
            onClick={(e) => toggleThemeWave(e.clientX, e.clientY)}
          />
          <div className="flex items-center justify-between gap-3 px-4 py-2.5">
            <span className="text-[15px] font-medium text-slate-700 dark:text-slate-100">
              {t.language}
            </span>
            <LanguageSwitcher />
          </div>
          <div className="my-1 border-t border-slate-100 dark:border-white/5" />
          <Row icon={LogOut} label={t.logout} onClick={logout} danger />
        </div>
      </div>
    </div>
  );
}
